import React, { useState, useMemo } from 'react';
import {
  WizardWorkspace,
  VerticalStack,
  HorizontalStack,
  CommerceObjectSelector,
  CommerceObjectMultiSelector,
  DescriptionText,
  PropertyGrid,
  PropertyItem,
  Surface,
  SectionSeparator,
} from '@workspace/silk-ui';
import { Info, Layout, Users, Store, ShieldCheck, FileText, Target } from 'lucide-react';
import type { AdminResellerEntitlement } from '@/lib/commerce';
import type { CommerceSelectOptionViewModel } from '@workspace/silk-ui';

const noCommissionPlanValue = '__none__';

export type EntitlementFormState = {
  publicationId: string;
  commissionPlanId: string;
  name: string;
  description: string;
  status: string;
  organizationIds: string[];
  sellingTenantIds: string[];
  storeIds: string[];
  agentUserIds: string[];
};

export interface EntitlementWizardEditorProps {
  isOpen: boolean;
  editingItem: AdminResellerEntitlement | null;
  form: EntitlementFormState;
  setForm: React.Dispatch<React.SetStateAction<EntitlementFormState>>;
  onSave: () => Promise<void>;
  onClose: () => void;
  saving: boolean;
  publicationOptions: CommerceSelectOptionViewModel[];
  commissionPlanOptions: CommerceSelectOptionViewModel[];
  tenantOptions: CommerceSelectOptionViewModel[];
  organizationOptions: CommerceSelectOptionViewModel[];
  storeOptions: CommerceSelectOptionViewModel[];
  agentOptions: CommerceSelectOptionViewModel[];
}

export function EntitlementWizardEditor({
  isOpen,
  editingItem,
  form,
  setForm,
  onSave,
  onClose,
  saving,
  publicationOptions,
  commissionPlanOptions,
  tenantOptions,
  organizationOptions,
  storeOptions,
  agentOptions,
}: EntitlementWizardEditorProps) {
  const [activeTabId, setActiveTabId] = useState('overview');

  const tabs = [
    { id: 'overview', label: 'Identity', icon: <Info className="h-4 w-4" /> },
    { id: 'publication', label: 'Catalog Source', icon: <Layout className="h-4 w-4" /> },
    { id: 'tenancy', label: 'Target Audience', icon: <Users className="h-4 w-4" />, count: form.organizationIds.length + form.sellingTenantIds.length },
    { id: 'context', label: 'Distribution', icon: <Store className="h-4 w-4" />, count: form.storeIds.length + form.agentUserIds.length },
  ];

  const selectedPublication = useMemo(
    () =>
      publicationOptions.find(opt => opt.value === form.publicationId) ||
      (editingItem && editingItem.publication_id === form.publicationId
        ? {
            value: editingItem.publication_id,
            label: editingItem.storefront_key?.trim() || editingItem.catalog_key?.trim() || editingItem.publication_id,
            description: editingItem.publication_scope || 'Saved publication',
            helperText: editingItem.publication_status || null,
            groupLabel: 'Saved',
            keywords: [
              editingItem.publication_id,
              editingItem.catalog_id,
              editingItem.catalog_key || '',
              editingItem.storefront_key || '',
              editingItem.publication_scope,
              editingItem.publication_status,
            ].filter(Boolean),
          }
        : undefined),
    [editingItem, publicationOptions, form.publicationId]
  );

  const summaryItems = [
    { 
      label: 'Publication', 
      value: selectedPublication?.label || 'None selected',
      complete: !!form.publicationId 
    },
    {
      label: 'Commission',
      value:
        commissionPlanOptions.find(option => option.value === (form.commissionPlanId || noCommissionPlanValue))?.label ||
        (form.commissionPlanId ? form.commissionPlanId : 'Not set'),
      complete: true,
    },
    { 
      label: 'Status', 
      value: form.status.toUpperCase(),
      complete: true 
    },
    { 
      label: 'Target Tenants', 
      value: `${form.sellingTenantIds.length} selected`,
      complete: form.sellingTenantIds.length > 0
    },
    { 
      label: 'Specific Context', 
      value: `${form.storeIds.length + form.agentUserIds.length} rules`,
      complete: true
    }
  ];

  const summaryCallout = !form.name 
    ? "Give your entitlement a clear name to help operators identify its purpose."
    : !form.publicationId 
    ? "Select a publication to define which catalog content is being shared."
    : form.sellingTenantIds.length === 0 && form.organizationIds.length === 0
    ? "This entitlement has no audience. Add tenants or organizations to grant access."
    : "Your entitlement configuration looks solid and is ready to be saved.";

  return (
    <WizardWorkspace
      isOpen={isOpen}
      title={editingItem ? 'Edit Entitlement' : 'New Entitlement'}
      subtitle={editingItem ? `Entitlement ID: ${editingItem.entitlement_id}` : "Configure reseller access rights for your catalog publications."}
      tabs={tabs}
      activeTabId={activeTabId}
      onTabChange={setActiveTabId}
      onSave={onSave}
      onClose={onClose}
      isSaving={saving}
      saveLabel={editingItem ? 'Update Entitlement' : 'Create Entitlement'}
      summaryItems={summaryItems}
      summaryCallout={summaryCallout}
    >
      {activeTabId === 'overview' && (
        <VerticalStack gap={6}>
          <div className="space-y-1">
            <h2 className="text-lg font-black tracking-tight">General Identity</h2>
            <DescriptionText>
              Provide a name and description for this entitlement. This information is internal and helps catalog owners manage reseller relationships.
            </DescriptionText>
          </div>

          <VerticalStack gap={4}>
            <div className="space-y-1.5">
              <label className="text-xs font-black uppercase tracking-widest text-muted-foreground/80">
                Entitlement Name
              </label>
              <input
                className="h-12 w-full rounded-lg border border-border/60 bg-background px-4 text-sm font-medium text-foreground outline-none transition-all focus:border-primary/40 focus:ring-4 focus:ring-primary/5"
                value={form.name}
                onChange={event => setForm(current => ({ ...current, name: event.target.value }))}
                placeholder="e.g., Global Reseller Program - North America"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-black uppercase tracking-widest text-muted-foreground/80">
                Description
              </label>
              <textarea
                className="min-h-32 w-full rounded-lg border border-border/60 bg-background px-4 py-3 text-sm font-medium text-foreground outline-none transition-all focus:border-primary/40 focus:ring-4 focus:ring-primary/5"
                value={form.description}
                onChange={event => setForm(current => ({ ...current, description: event.target.value }))}
                placeholder="Optional notes for operators regarding the intent of this entitlement."
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-black uppercase tracking-widest text-muted-foreground/80">
                Operating Status
              </label>
              <select
                className="h-12 w-full rounded-lg border border-border/60 bg-background px-4 text-sm font-medium text-foreground outline-none transition-all focus:border-primary/40 focus:ring-4 focus:ring-primary/5"
                value={form.status}
                onChange={event => setForm(current => ({ ...current, status: event.target.value }))}
              >
                <option value="active">Active - Entitlement is live and enforced</option>
                <option value="inactive">Inactive - Entitlement is drafted but not enforced</option>
              </select>
            </div>
          </VerticalStack>
        </VerticalStack>
      )}

      {activeTabId === 'publication' && (
        <VerticalStack gap={6}>
          <div className="space-y-1">
            <h2 className="text-lg font-black tracking-tight">Catalog Publication</h2>
            <DescriptionText>
              Select the publication that this entitlement grants access to. Entitlements are tied to specific publication releases.
            </DescriptionText>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-black uppercase tracking-widest text-muted-foreground/80">
              Select Publication
            </label>
            <CommerceObjectSelector
              options={publicationOptions}
              value={form.publicationId}
              onChange={value => setForm(current => ({ ...current, publicationId: value }))}
              placeholder="Search and select a publication..."
            />
            <p className="mt-2 text-[10px] font-bold text-muted-foreground">
              Existing entitlements can be moved to another publication owned by the same catalog owner tenant.
            </p>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-black uppercase tracking-widest text-muted-foreground/80">
              Default Commission Plan
            </label>
            <CommerceObjectSelector
              options={commissionPlanOptions}
              value={form.commissionPlanId || noCommissionPlanValue}
              onChange={value => setForm(current => ({ ...current, commissionPlanId: value === noCommissionPlanValue ? '' : value }))}
              placeholder="Search and select a commission plan..."
            />
            <p className="mt-2 text-[10px] font-bold text-muted-foreground">
              This is the reseller default for the entitlement. Store and agent commission assignments still override it.
            </p>
          </div>

          {selectedPublication && (
            <Surface className="mt-4 border-border/60 bg-muted/5 p-6">
              <VerticalStack gap={4}>
                <HorizontalStack className="items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <FileText className="h-5 w-5" />
                  </div>
                  <VerticalStack gap={1}>
                    <p className="text-sm font-black">{selectedPublication.label}</p>
                    <DescriptionText className="text-xs">
                      {selectedPublication.description}
                    </DescriptionText>
                  </VerticalStack>
                </HorizontalStack>
                
                <SectionSeparator />
                
                <PropertyGrid cols={3}>
                  <PropertyItem label="Publication ID" value={form.publicationId} />
                  <PropertyItem label="Status" value={selectedPublication.helperText || 'Unknown'} />
                  <PropertyItem label="Scope" value={selectedPublication.description || 'Public'} />
                </PropertyGrid>
              </VerticalStack>
            </Surface>
          )}
        </VerticalStack>
      )}

      {activeTabId === 'tenancy' && (
        <VerticalStack gap={6}>
          <div className="space-y-1">
            <h2 className="text-lg font-black tracking-tight">Target Audience</h2>
            <DescriptionText>
              Define the organizations and tenants that are granted access by this entitlement. Access is additive across all selected targets.
            </DescriptionText>
          </div>

          <VerticalStack gap={4}>
            <div className="space-y-1.5">
              <label className="text-xs font-black uppercase tracking-widest text-muted-foreground/80">
                Organizations
              </label>
              <CommerceObjectMultiSelector
                options={organizationOptions}
                values={form.organizationIds}
                onChange={values => setForm(current => ({ ...current, organizationIds: values }))}
                placeholder="Select organizations..."
                noneSelectedLabel="No specific organizations targeted"
              />
              <DescriptionText className="text-[10px]">
                Grant access to all tenants belonging to these organizations.
              </DescriptionText>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-black uppercase tracking-widest text-muted-foreground/80">
                Specific Tenants
              </label>
              <CommerceObjectMultiSelector
                options={tenantOptions}
                values={form.sellingTenantIds}
                onChange={values => setForm(current => ({ ...current, sellingTenantIds: values }))}
                placeholder="Select individual tenants..."
                noneSelectedLabel="No specific tenants targeted"
              />
              <DescriptionText className="text-[10px]">
                Grant access to individual tenants regardless of their organization.
              </DescriptionText>
            </div>
          </VerticalStack>

          <Surface className="border-dashed border-primary/20 bg-primary/5 p-4">
            <HorizontalStack gap={3} className="items-center">
              <Target className="h-4 w-4 text-primary" />
              <p className="text-xs font-bold text-primary/80">
                Total Audience Impact: {form.organizationIds.length} Orgs, {form.sellingTenantIds.length} Tenants.
              </p>
            </HorizontalStack>
          </Surface>
        </VerticalStack>
      )}

      {activeTabId === 'context' && (
        <VerticalStack gap={6}>
          <div className="space-y-1">
            <h2 className="text-lg font-black tracking-tight">Distribution Context</h2>
            <DescriptionText>
              Fine-tune access by restricting it to specific stores or agent users. If none are selected, access is granted across the entire tenant context.
            </DescriptionText>
          </div>

          <VerticalStack gap={4}>
            <div className="space-y-1.5">
              <label className="text-xs font-black uppercase tracking-widest text-muted-foreground/80">
                Stores
              </label>
              <CommerceObjectMultiSelector
                options={storeOptions}
                values={form.storeIds}
                onChange={values => setForm(current => ({ ...current, storeIds: values }))}
                placeholder="Select stores..."
                noneSelectedLabel="Available across all stores"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-black uppercase tracking-widest text-muted-foreground/80">
                Agents / Users
              </label>
              <CommerceObjectMultiSelector
                options={agentOptions}
                values={form.agentUserIds}
                onChange={values => setForm(current => ({ ...current, agentUserIds: values }))}
                placeholder="Select agents..."
                noneSelectedLabel="Available to all agents"
              />
            </div>
          </VerticalStack>

          <Surface className="border-border/60 bg-muted/5 p-6">
            <VerticalStack gap={3}>
              <HorizontalStack gap={2} className="items-center text-muted-foreground">
                <ShieldCheck className="h-4 w-4" />
                <p className="text-xs font-black uppercase tracking-widest">Enforcement Preview</p>
              </HorizontalStack>
              <PropertyGrid cols={2}>
                <PropertyItem label="Selected Stores" value={`${form.storeIds.length || 'All'}`} />
                <PropertyItem label="Selected Agents" value={`${form.agentUserIds.length || 'All'}`} />
              </PropertyGrid>
            </VerticalStack>
          </Surface>
        </VerticalStack>
      )}
    </WizardWorkspace>
  );
}
